import React from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

interface ModernPieChartProps {
  title?: string;
  data: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  formatValue?: (value: number) => string;
  formatTooltip?: (value: number, name: string, percent: number) => string;
  innerRadius?: number;
  outerRadius?: number;
}

const ModernPieChart: React.FC<ModernPieChartProps> = ({
  title,
  data,
  formatValue,
  formatTooltip,
  innerRadius = 60,
  outerRadius = 80
}) => {
  const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      
      // Filter data to only include items with value > 0
      const filteredData = data.filter(item => item.value > 0);
      const total = filteredData.reduce((sum, item) => sum + item.value, 0);
      const percent = ((entry.value as number) / total) * 100;
      
      return (
        <div 
          className="bg-white p-4 rounded-lg shadow-xl border border-gray-200"
          style={{ 
            zIndex: 9999, 
            pointerEvents: 'auto',
            minWidth: '200px'
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div 
              className="w-4 h-4 rounded-full flex-shrink-0" 
              style={{ backgroundColor: entry.payload.color }}
            ></div>
            <p className="text-gray-700 font-medium">{entry.name}</p>
          </div>
          <div className="space-y-2">
            <p className="text-sm">
              <span className="font-medium">Valor: </span>
              <span className="font-bold">
                {formatTooltip 
                  ? formatTooltip(entry.value as number, entry.name as string, percent) 
                  : formatValue 
                    ? formatValue(entry.value as number)
                    : entry.value}
              </span>
            </p>
            <p className="text-sm">
              <span className="font-medium">Porcentaje: </span>
              <span className="font-bold">{percent.toFixed(1)}%</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Filter data to only include items with value > 0
  const filteredData = data.filter(item => item.value > 0);

  return (
    <div className="w-full h-full">
      {title && (
        <h3 className="text-center text-lg font-medium text-gray-800 mb-4">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart
          margin={{
            top: 20,
            right: 50,
            left: 50,
            bottom: 10,
          }}
        >
          <Pie
            data={filteredData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={outerRadius}
            innerRadius={innerRadius}
            fill="#8884d8"
            dataKey="value"
            animationDuration={1200}
            animationEasing="ease-out"
            animationBegin={300}
          >
            {filteredData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color} 
                className="hover:filter hover:brightness-110 transition-all duration-300"
              />
            ))}
          </Pie>
          <Tooltip 
            content={<CustomTooltip />} 
            wrapperStyle={{ zIndex: 9999, pointerEvents: 'auto' }}
            isAnimationActive={false}
          />
          <Legend 
            layout="horizontal" 
            verticalAlign="bottom" 
            align="center"
            formatter={(value) => (
              <span className="text-sm text-gray-700">
                {value}
              </span>
            )}
          />
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ModernPieChart;